import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Users, Plus, X, Search, Building2, User, Star } from 'lucide-react';
import api from '../../api/client';

const PARTY_ROLES = {
  case_involvement: [
    'Adjuster', 'Senior Adjuster', 'Claims Manager', 'Defense Attorney',
    'Opposing Counsel', 'Expert Witness', 'Fact Witness', 'Treating Physician',
    'Employer Representative', 'Guardian', 'Interpreter', 'Lien Holder', 'Other',
  ],
  employment: [
    'Adjuster', 'Senior Adjuster', 'Claims Manager', 'Attorney',
    'Paralegal', 'Office Manager', 'Receptionist', 'Other',
  ],
};

export default function PartiesPanel({ recordId, recordTypeSlug, tenantId }) {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  // Determine mode: case parties, contact employment, or company contacts
  const isCaseRecord = ['intake', 'pi_litigation_mva'].includes(recordTypeSlug);
  const isContact = recordTypeSlug === 'contact';
  const isCompany = recordTypeSlug === 'company';

  const title = isContact ? 'Employment' : isCompany ? 'Contacts' : 'Parties';
  const relationType = (isContact || isCompany) ? 'employment' : 'case_involvement';
  const searchType = isCompany ? 'contact' : isContact ? 'company' : 'contact';
  const roles = PARTY_ROLES[relationType] || [];
  const icon = isCompany ? User : isContact ? Building2 : Users;
  const Icon = icon;

  const fetchParties = useCallback(async () => {
    try {
      let url;
      if (isContact) {
        url = `/parties/employment/${recordId}`;
      } else {
        url = `/parties/${recordId}`;
      }
      const { data } = await api.get(url);
      setParties(data.parties || []);
    } catch (err) {
      console.error('[PartiesPanel] load error:', err.message);
    } finally {
      setLoading(false);
    }
  }, [recordId, isContact]);

  useEffect(() => { fetchParties(); }, [fetchParties]);

  const handleRemove = async (relationId) => {
    if (!window.confirm('Remove this party?')) return;
    try {
      await api.delete(`/parties/${relationId}`);
      toast.success('Party removed');
      fetchParties();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove');
    }
  };

  if (!isCaseRecord && !isContact && !isCompany) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <span className="text-xs text-gray-400">({parties.length})</span>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium">
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>

      {loading ? (
        <div className="p-4"><div className="animate-pulse h-8 bg-gray-100 rounded" /></div>
      ) : parties.length === 0 ? (
        <div className="p-4 text-center text-xs text-gray-400 italic">
          No {title.toLowerCase()} linked yet
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {parties.map(p => (
            <div key={p.relation_id} className="px-4 py-2.5 flex items-center justify-between group hover:bg-gray-50">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {(isContact || isCompany)
                    ? <Building2 className="h-3.5 w-3.5 text-gray-400" />
                    : <User className="h-3.5 w-3.5 text-gray-400" />
                  }
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <a href={`/records/${p.record_type_slug || 'contact'}/${isContact ? p.company_id || p.target_record_id : p.contact_id || p.source_record_id}`}
                      className="text-sm font-medium text-gray-900 hover:text-brand-600 truncate">
                      {isContact ? p.company_name : p.contact_name}
                    </a>
                    {p.is_primary && <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <span>{p.reference_number}</span>
                    {p.role && (
                      <>
                        <span className="text-gray-300">&middot;</span>
                        <span className="text-gray-600 font-medium">{p.role}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={() => handleRemove(p.relation_id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-500 transition-all">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Party Modal */}
      {showAdd && (
        <AddPartyModal
          recordId={recordId}
          relationType={relationType}
          searchType={searchType}
          roles={roles}
          isContact={isContact}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); fetchParties(); }}
        />
      )}
    </div>
  );
}

function AddPartyModal({ recordId, relationType, searchType, roles, isContact, onClose, onAdded }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/records?recordType=${searchType}&search=${encodeURIComponent(search)}&limit=10`);
        setResults(data.records || data || []);
      } catch (err) {
        console.error('[PartySearch]', err.message);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, searchType]);

  const handleAdd = async () => {
    if (!selected) return toast.error('Select a record first');
    setSaving(true);
    try {
      const finalRole = role === 'Other' ? customRole : role;
      await api.post('/parties', {
        sourceRecordId: isContact ? recordId : selected.id,
        targetRecordId: isContact ? selected.id : recordId,
        relationType,
        role: finalRole || null,
        isPrimary,
      });
      toast.success('Party added');
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Add {relationType === 'employment' ? 'Employment' : 'Party'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Search */}
          {!selected ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={`Search ${searchType}s...`} autoFocus
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              {searching && <p className="text-xs text-gray-400 text-center py-2">Searching...</p>}
              {results.length > 0 && (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-50">
                  {results.map(r => (
                    <button key={r.id} onClick={() => setSelected(r)}
                      className="w-full text-left px-3 py-2.5 hover:bg-brand-50 transition-colors">
                      <span className="text-sm font-medium text-gray-800">{r.title || 'Untitled'}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {r.reference_number && <span className="text-[10px] text-gray-400">{r.reference_number}</span>}
                        {r.record_type_name && <span className="text-[10px] text-gray-300">{r.record_type_name}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {search.length >= 2 && !searching && results.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-2">No results found</p>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">{selected.title}</p>
                <p className="text-xs text-gray-400">{selected.reference_number}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Select role...</option>
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {role === 'Other' && (
            <input type="text" value={customRole} onChange={e => setCustomRole(e.target.value)}
              placeholder="Enter custom role..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          )}

          {/* Primary */}
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300" />
            Primary
          </label>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="text-sm px-3 py-2 border border-gray-200 rounded-lg text-gray-600">Cancel</button>
            <button onClick={handleAdd} disabled={!selected || saving}
              className="text-sm bg-brand-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-brand-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Party'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
